import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { useDashboard } from "../context/DashboardContext";
import { db } from "../config/fbConfig";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import "../styles/Home.css";
import Loader from "./Loader";
import Modal from 'react-modal';
import Logo from '../images/school_logo.png';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import { SCHOOL_ACRONYM } from '../Variables.js';
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

const Dashboard = () => {
  const { user, userData } = useUserAuth();
  const { 
    dashboardData, 
    setDashboardData, 
    loading, 
    setLoading 
  } = useDashboard();
  const navigate = useNavigate();
  // Report states
const [showDailyReportModal, setShowDailyReportModal] = useState(false);
const [showMonthlyReportModal, setShowMonthlyReportModal] = useState(false);
const [selectedDate, setSelectedDate] = useState('');
const [selectedMonth, setSelectedMonth] = useState('');
const [generatingReport, setGeneratingReport] = useState(false);
const [availableMonths, setAvailableMonths] = useState([]);
const [error, setError] = useState("");
const [showModal, setShowModal] = useState(false);

// Fetch available months
const fetchAvailableMonths = async () => {
  try {
    const historyRef = collection(db, "paymentHistory");
    const historySnap = await getDocs(historyRef);
    const history = historySnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    const months = history.reduce((acc, payment) => {
      const date = payment.timestamp.toDate();
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!acc.includes(monthYear)) {
        acc.push(monthYear);
      }
      return acc;
    }, []);

    // Get current year months so far
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const currentYearMonths = [];
    for (let i = 1; i <= currentMonth; i++) {
      const monthKey = `${currentYear}-${String(i).padStart(2, '0')}`;
      if (!currentYearMonths.includes(monthKey)) {
        currentYearMonths.push(monthKey);
      }
    }

    // Combine and sort
    const allMonths = [...new Set([...months, ...currentYearMonths])];
    allMonths.sort((a, b) => b.localeCompare(a));
    
    setAvailableMonths(allMonths);
  } catch (error) {
    console.error("Error fetching available months:", error);
  }
};

    const generateDailyReportPDF = async () => {
    if (!selectedDate) {
      setError("Please select a date");
      setShowModal(true);
      return;
    }

    try {
      setGeneratingReport(true);
      const historyRef = collection(db, "paymentHistory");

      const selectedDateObj = new Date(selectedDate);
      const startOfDay = new Date(selectedDateObj.setHours(0, 0, 0, 0));
      const endOfDay = new Date(selectedDateObj.setHours(23, 59, 59, 999));

      const q = query(
        historyRef,
        where("timestamp", ">=", startOfDay),
        where("timestamp", "<=", endOfDay)
      );

      const historySnap = await getDocs(q);
      const payments = historySnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(payment => payment.type !== 'fee_update');

      if (payments.length === 0) {
        setError("No payments found for the selected date");
        setShowModal(true);
        setGeneratingReport(false);
        setShowDailyReportModal(false);
        setSelectedDate('');
        return;
      }

      // Calculate totals by payment method
      const methodTotals = payments.reduce((acc, payment) => {
        const method = (payment.paymentMethod || 'Transfer').trim();
        const amount = Number(payment.amountPaid) || 0;
        acc[method] = (acc[method] || 0) + amount;
        return acc;
      }, {});

      const cashTotal = methodTotals['Cash'] || 0;
      const transferTotal = (methodTotals['Transfer'] || 0) + (methodTotals['Bank Transfer'] || 0) + (methodTotals['transfer'] || 0);
      const posTotal = (methodTotals['POS'] || 0) + (methodTotals['Pos'] || 0) + (methodTotals['pos'] || 0);

      const doc = new jsPDF();
      const currentDate = new Date().toLocaleString();

      doc.setFontSize(18);
      doc.text(`${SCHOOL_ACRONYM} Daily Payment Report`, 15, 15);
      doc.setFontSize(12);
      doc.text(`Date: ${selectedDate}`, 15, 25);
      doc.text(`Generated on: ${currentDate}`, 15, 32);
      doc.text(`Cashier: ${userData?.firstName || 'Unknown'} ${userData?.lastName || ''}`, 15, 39);

      let totalAmount = 0;
      const tableData = payments.map((payment, index) => {
        const amount = Number(payment.amountPaid) || 0;
        totalAmount += amount;

        const method = (payment.paymentMethod || 'Transfer').trim();
        const displayMethod = method === 'Transfer' || method === 'Bank Transfer' || method.toLowerCase() === 'transfer'
          ? 'Transfer'
          : method === 'POS' || method === 'Pos' || method.toLowerCase() === 'pos'
            ? 'POS'
            : method;

        return [
          index + 1,
          payment.studentName || 'N/A',
          payment.className || 'N/A',
          payment.payerName || 'N/A',
          payment.cashierName || 'N/A',
          displayMethod,
          (payment.paymentFor || ['School fees']).join(', '),
          amount.toLocaleString(),
          payment.timestamp?.toDate ? new Date(payment.timestamp.toDate()).toLocaleString() : 'N/A'
        ];
      });

      const totalsRow = ['', 'TOTAL', '', '', '', '', '', totalAmount.toLocaleString(), ''];

      autoTable(doc, {
        head: [['S/N', 'Student', 'Class', 'Payer', 'Cashier', 'Method', 'Payment For', 'Amount', 'Date/Time']],
        body: [...tableData, totalsRow],
        startY: 45,
        styles: { cellPadding: 2, fontSize: 8, valign: 'middle', textColor: 0, },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 25 },
          2: { cellWidth: 18 },
          3: { cellWidth: 22 },
          4: { cellWidth: 22 },
          5: { cellWidth: 18 },  // Payment Method column
          6: { cellWidth: 28 },
          7: { cellWidth: 15 },
          8: { cellWidth: 28 }
        },
        didDrawPage: function (data) {
          let y = data.cursor.y + 15;

          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(`Total Amount Collected: N ${totalAmount.toLocaleString()}`, 14, y);
          y += 12;

          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text('Payment Method Summary', 14, y);
          y += 7;

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');

          if (cashTotal > 0) {
            doc.text('Cash:', 20, y);
            doc.text(`N ${cashTotal.toLocaleString()}`, 180, y, { align: 'right' });
            y += 6;
          }
          if (transferTotal > 0) {
            doc.text('Transfer:', 20, y);
            doc.text(`N ${transferTotal.toLocaleString()}`, 180, y, { align: 'right' });
            y += 6;
          }
          if (posTotal > 0) {
            doc.text('POS:', 20, y);
            doc.text(`N ${posTotal.toLocaleString()}`, 180, y, { align: 'right' });
            y += 8;
          }

          doc.setLineWidth(0.5);
          doc.line(14, y - 2, 196, y - 2);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.text('GRAND TOTAL:', 14, y + 6);
          doc.text(`N ${totalAmount.toLocaleString()}`, 180, y + 6, { align: 'right' });
        }
      });

      doc.save(`Daily_Report_${selectedDate.replace(/-/g, '_')}.pdf`);
      setSelectedDate('');
      setShowDailyReportModal(false);
    } catch (error) {
      console.error("Error generating daily report:", error);
      setError(`Error: ${error.message}`);
      setShowModal(true);
    } finally {
      setGeneratingReport(false);
    }
  };

  const generateMonthlyReportPDF = async () => {
    if (!selectedMonth) {
      setError("Please select a month");
      setShowModal(true);
      return;
    }

    try {
      setGeneratingReport(true);
      const historyRef = collection(db, "paymentHistory");

      const [year, month] = selectedMonth.split('-');
      const yearNum = parseInt(year);
      const monthNum = parseInt(month);
      const startOfMonth = new Date(yearNum, monthNum - 1, 1);
      const endOfMonth = new Date(yearNum, monthNum, 0, 23, 59, 59, 999);

      const q = query(
        historyRef,
        where("timestamp", ">=", startOfMonth),
        where("timestamp", "<=", endOfMonth)
      );

      const historySnap = await getDocs(q);
      const payments = historySnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(payment => payment.type !== 'fee_update');

      payments.sort((a, b) => (b.timestamp?.toDate?.() || 0) - (a.timestamp?.toDate?.() || 0));

      if (payments.length === 0) {
        setError("No payments found for the selected month");
        setShowModal(true);
        setGeneratingReport(false);
        setShowMonthlyReportModal(false);
        setSelectedMonth('');
        return;
      }

      // Calculate totals by payment method
      const methodTotals = payments.reduce((acc, payment) => {
        const method = (payment.paymentMethod || 'Transfer').trim();
        const amount = Number(payment.amountPaid) || 0;
        acc[method] = (acc[method] || 0) + amount;
        return acc;
      }, {});

      const cashTotal = methodTotals['Cash'] || 0;
      const transferTotal = (methodTotals['Transfer'] || 0) + (methodTotals['Bank Transfer'] || 0) + (methodTotals['transfer'] || 0);
      const posTotal = (methodTotals['POS'] || 0) + (methodTotals['Pos'] || 0) + (methodTotals['pos'] || 0);

      const doc = new jsPDF();
      const currentDate = new Date().toLocaleString();
      const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

      doc.setFontSize(18);
      doc.text(`${SCHOOL_ACRONYM} Monthly Payment Report`, 15, 15);
      doc.setFontSize(12);
      doc.text(`Month: ${monthNames[monthNum - 1]} ${year}`, 15, 25);
      doc.text(`Generated on: ${currentDate}`, 15, 32);
      doc.text(`Cashier: ${userData?.firstName || 'Unknown'} ${userData?.lastName || ''}`, 15, 39);

      let totalAmount = 0;
      const tableData = payments.map((payment, index) => {
        const amount = Number(payment.amountPaid) || 0;
        totalAmount += amount;

        const method = (payment.paymentMethod || 'Transfer').trim();
        const displayMethod = method === 'Transfer' || method === 'Bank Transfer' || method.toLowerCase() === 'transfer'
          ? 'Transfer'
          : method === 'POS' || method === 'Pos' || method.toLowerCase() === 'pos'
            ? 'POS'
            : method;

        return [
          index + 1,
          payment.studentName || 'N/A',
          payment.className || 'N/A',
          payment.payerName || 'N/A',
          payment.cashierName || 'N/A',
          displayMethod,
          (payment.paymentFor || ['School fees']).join(', '),
          amount.toLocaleString(),
          payment.timestamp?.toDate ? new Date(payment.timestamp.toDate()).toLocaleDateString() : 'N/A'
        ];
      });

      const totalsRow = ['', 'TOTAL', '', '', '', '', '', totalAmount.toLocaleString(), ''];

      autoTable(doc, {
        head: [['S/N', 'Student', 'Class', 'Payer', 'Cashier', 'Method', 'Payment For', 'Amount', 'Date']],
        body: [...tableData, totalsRow],
        startY: 45,
        styles: { cellPadding: 2, fontSize: 8, valign: 'middle', textColor: 0, },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 25 },
          2: { cellWidth: 18 },
          3: { cellWidth: 22 },
          4: { cellWidth: 22 },
          5: { cellWidth: 18 },  // Payment Method column
          6: { cellWidth: 28 },
          7: { cellWidth: 15 },
          8: { cellWidth: 20 }
        },
        didDrawPage: function (data) {
          let y = data.cursor.y + 15;

          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(`Total Amount Collected: N ${totalAmount.toLocaleString()}`, 14, y);
          y += 12;

          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text('Payment Method Summary', 14, y);
          y += 7;

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');

          if (cashTotal > 0) {
            doc.text('Cash:', 20, y);
            doc.text(`N ${cashTotal.toLocaleString()}`, 180, y, { align: 'right' });
            y += 6;
          }
          if (transferTotal > 0) {
            doc.text('Transfer:', 20, y);
            doc.text(`N ${transferTotal.toLocaleString()}`, 180, y, { align: 'right' });
            y += 6;
          }
          if (posTotal > 0) {
            doc.text('POS:', 20, y);
            doc.text(`N ${posTotal.toLocaleString()}`, 180, y, { align: 'right' });
            y += 8;
          }

          doc.setLineWidth(0.5);
          doc.line(14, y - 2, 196, y - 2);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.text('GRAND TOTAL:', 14, y + 6);
          doc.text(`N ${totalAmount.toLocaleString()}`, 180, y + 6, { align: 'right' });
        }
      });

      doc.save(`Monthly_Report_${selectedMonth}.pdf`);
      setSelectedMonth('');
      setShowMonthlyReportModal(false);
    } catch (error) {
      console.error("Error generating monthly report:", error);
      setError(`Error: ${error.message}`);
      setShowModal(true);
    } finally {
      setGeneratingReport(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      // Check if data is still valid (fetched within last 5 minutes)
      const now = new Date();
      const lastFetched = dashboardData.lastFetched ? new Date(dashboardData.lastFetched) : null;
      const isDataStale = !lastFetched || (now - lastFetched) > 5 * 60 * 1000;
      
      if (!isDataStale && dashboardData.currentTerm) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Fetch current term document
        const currentTermDoc = await getDoc(doc(db, "current-term", "current"));
        const currentTermData = currentTermDoc.data();
        const currentSessionId = currentTermData?.sessionId;
        const currentTermId = currentTermData?.termId;

        if (!currentSessionId || !currentTermId) {
          console.error("Current session or term not found");
          setLoading(false);
          return;
        }

        // Check if term changed
        const termChanged = 
          dashboardData.currentTerm?.sessionId !== currentSessionId || 
          dashboardData.currentTerm?.termId !== currentTermId;

        if (!isDataStale && !termChanged) {
          setLoading(false);
          return;
        }

        // Fetch section names
        const sectionsQuery = query(collection(db, "sections"));
        const sectionsSnapshot = await getDocs(sectionsQuery);
        const sectionsData = Object.fromEntries(
          sectionsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        // Fetch session and term names
        const sessionsQuery = query(
          collection(db, "sessions"),
          where("__name__", "==", currentSessionId)
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);
        const sessionsData = Object.fromEntries(
          sessionsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        const termsQuery = query(
          collection(db, "terms"),
          where("__name__", "==", currentTermId)
        );
        const termsSnapshot = await getDocs(termsQuery);
        const termsData = Object.fromEntries(
          termsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        // Fetch all classes for current term
        const classesQuery = query(
          collection(db, "classes"),
          where("session", "==", currentSessionId),
          where("term", "==", currentTermId)
        );
        const classesSnapshot = await getDocs(classesQuery);
        const currentTermClassIds = classesSnapshot.docs.map((doc) => doc.id);
        const classMap = Object.fromEntries(
          classesSnapshot.docs.map((doc) => {
            const data = doc.data();
            return [
              doc.id,
              {
                name: data.name || "Unknown Class",
                students: data.students || [],
                section: data.section || "",
              },
            ];
          })
        );

        if (
          (userData?.status === "Admin") || 
          (userData?.status === "Cashier" && userData?.isActive === true)
        ) {
          // ADMIN/CASHIER VIEW
          const classCount = classesSnapshot.size;

          const sectionSet = new Set(
            classesSnapshot.docs.map((doc) => doc.data().section)
          );
          const sectionCount = sectionSet.size;

          const teachersQuery = query(
            collection(db, "users"),
            where("status", "==", "Teacher")
          );
          const teachersSnapshot = await getDocs(teachersQuery);

          const activeTeachers = teachersSnapshot.docs.filter(doc => {
            const teacherData = doc.data();
            const assignments = teacherData.teachingAssignments || [];
            return assignments.some(assignment => 
              currentTermClassIds.includes(assignment.classId)
            );
          });

          const activeTeachersWithDetails = await Promise.all(
            activeTeachers.map(async teacherDoc => {
              const teacherData = teacherDoc.data();
              const assignments = teacherData.teachingAssignments || [];
              
              const currentTermAssignments = assignments.filter(assignment => 
                currentTermClassIds.includes(assignment.classId)
              );
              
              const currentTermSubjects = [...new Set(
                currentTermAssignments.map(a => a.subjectId))
              ];
              
              return {
                id: teacherDoc.id,
                ...teacherData,
                currentTermSubjectsCount: currentTermSubjects.length,
                currentTermClassCount: [...new Set(
                  currentTermAssignments.map(a => a.classId)
                )].length
              };
            })
          );

          const allStudentIds = new Set();
          Object.values(classMap).forEach((cls) => {
            cls.students.forEach((studentId) => allStudentIds.add(studentId));
          });

          let studentsData = [];
          if (allStudentIds.size > 0) {
            const studentIdArray = Array.from(allStudentIds);
            const chunks = [];
            for (let i = 0; i < studentIdArray.length; i += 10) {
              chunks.push(studentIdArray.slice(i, i + 10));
            }

            for (const chunk of chunks) {
              const studentsQuery = query(
                collection(db, "students"),
                where("__name__", "in", chunk)
              );
              const studentsSnapshot = await getDocs(studentsQuery);
              studentsData = [
                ...studentsData,
                ...studentsSnapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                })),
              ];
            }
          }

          const studentGenderMap = Object.fromEntries(
            studentsData.map((student) => [student.id, student.gender || ""])
          );

          let maleCount = 0;
          let femaleCount = 0;
          const studentsPerClass = {};
          let totalExpected = 0;
          let totalActual = 0;
          const financialStatsByClass = {};

          Object.entries(classMap).forEach(([classId, cls]) => {
            studentsPerClass[classId] = { male: 0, female: 0, total: 0, sectionName: sectionsData[cls.section] || "Unknown Section" };
            financialStatsByClass[classId] = { 
              expected: 0, 
              actual: 0, 
              balance: 0,
              className: cls.name || "Unknown Class",
              sectionId: cls.section || "",
              sectionName: sectionsData[cls.section] || "Unknown Section"
            };

            cls.students.forEach((studentId) => {
              const gender = studentGenderMap[studentId] || "";
              
              if (gender.toLowerCase() === "male") {
                studentsPerClass[classId].male++;
                maleCount++;
              } else if (gender.toLowerCase() === "female") {
                studentsPerClass[classId].female++;
                femaleCount++;
              }

              studentsPerClass[classId].total = studentsPerClass[classId].male + studentsPerClass[classId].female;

              const student = studentsData.find(s => s.id === studentId);
              if (student && student.enrollments) {
                const enrollment = student.enrollments.find(e => e.classId === classId) || { fees: 0, paid: 0 };
                const fees = Number(enrollment.fees) || 0;
                const paid = Number(enrollment.paid) || 0;
                const balance = fees - paid;

                financialStatsByClass[classId].expected += fees;
                financialStatsByClass[classId].actual += paid;
                financialStatsByClass[classId].balance += balance;

                totalExpected += fees;
                totalActual += paid;
              }
            });
          });

          const studentsByClass = Object.entries(studentsPerClass)
            .map(([classId, counts]) => ({
              className: classMap[classId].name,
              section: classMap[classId].section,
              sectionName: sectionsData[classId] || "Unknown Section",
              ...counts,
            }))
            .filter((cls) => cls.total > 0)
            .sort((a, b) => a.className.localeCompare(b.className));

          const financialStatsByClassArray = Object.entries(financialStatsByClass)
            .map(([classId, stats]) => ({
              className: classMap[classId].name,
              section: classMap[classId].section,
              ...stats,
            }))
            .sort((a, b) => a.className.localeCompare(b.className));

          setDashboardData(prev => ({
            ...prev,
            adminStats: {
              teacherCount: activeTeachers.length,
              activeTeachers: activeTeachersWithDetails,
              totalSubjectsCurrentTerm: activeTeachersWithDetails.reduce(
                (sum, teacher) => sum + teacher.currentTermSubjectsCount, 0
              ),
              studentCount: {
                male: maleCount,
                female: femaleCount,
                total: maleCount + femaleCount,
              },
              classCount,
              studentsByClass,
              sectionCount,
              financialStats: {
                expected: totalExpected,
                actual: totalActual,
                balance: totalExpected - totalActual,
              },
              financialStatsByClass: financialStatsByClassArray,
            },
            lastFetched: new Date().toISOString(),
            currentTerm: {
              sessionId: currentSessionId,
              termId: currentTermId
            }
          }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userData) {
      fetchData();
    }
  }, [userData]);

  // Fetch available months when component mounts
useEffect(() => {
  if (userData?.level === "Supreme" || userData?.status === "Cashier") {
    fetchAvailableMonths();
  }
}, [userData]);

  if (loading || !userData) {
    return <Loader text={"LOADING"} />;
  }

  const {
    adminStats,
  } = dashboardData;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="new-dashboard">
      {userData.status === "Admin" || userData.status === "Cashier" ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <div className="column align-center justify-center">
              <img src={Logo} className="middle-logo" alt="logo" />
            </div>
            <Link to="#" onClick={() => window.location.reload()} className="home-link">
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>
          <div className="scroll-fixed scroll-fixed5">
              <p className="caps login-heading page-heading page-heading-d">SCHOOL OVERVIEW</p>
              <div className="stats-grid">
                <div
                  className="stat-content"
                  onClick={() => navigate("/sections")}
                >
                  <i className="fa-solid fa-school" style={{fontSize: '3rem', color: '#9B59B6'}}></i>
                  <p className="stat-value"><span className="bold italics size2">{adminStats.sectionCount}</span><br />Sections</p>
                </div>
                <div
                  className="stat-content"
                  onClick={() => navigate("/classes")}
                >
                  <i className="fa-solid fa-chalkboard-user size1" style={{fontSize: '3rem', color: '#5C9E7D'}}></i>
                  <p className="stat-value"><span className="bold italics size2">{adminStats.classCount}</span><br />Classes</p>              
                </div>
                <div
                  className="stat-content"
                  onClick={() => navigate("/teachers")}
                >
                  <i className="fa-solid fa-person-chalkboard size1" style={{fontSize: '3rem', color: '#3A7CA5'}}></i>
                  <p className="stat-value"><span className="bold italics size2">{adminStats.teacherCount}</span><br />Active Teachers</p>
                </div>
          
                <div
                  className="stat-content"
                  onClick={() => navigate("/students")}
                >
                  <i className="fa-solid fa-user-graduate size1" style={{fontSize: '3rem', color: '#E56B6F'}}></i>
                  <p className="stat-value"><span className="bold italics size2">{adminStats.studentCount.total}</span><br />Students</p>                
                </div>    
                {(userData.status === "Cashier" || userData.level === "Supreme") && (
                  <>
                    <button onClick={() => setShowDailyReportModal(true)} className="stat-content">
                      <i className="fa-solid fa-file-invoice" style={{fontSize: '3rem', color: '#F39C12'}}></i>
                      <p className="stat-value">                
                        <span className="bold italics size2">Daily</span>
                        <br />
                        Payments Report
                      </p>
                    </button>

                    <button onClick={() => setShowMonthlyReportModal(true)} className="stat-content">
                      <i className="fa-solid fa-calendar-alt" style={{fontSize: '3rem', color: '#00CED1'}}></i>
                      <p className="stat-value">                
                        <span className="bold italics size2">Monthly</span>
                        <br />
                        Payments Report
                      </p>
                    </button>
                  </> 
                )}                   
              </div>
            <p className="caps login-heading page-heading page-heading-d">STUDENTS BY CLASS</p>
        
            <div className="stat-content-by-class">
              <p className="bold stat-heading-d">All Classes</p>
              <p className="male-female-stat"><span className="bold italics">{adminStats.studentCount.total}</span>&nbsp;Students</p>
              <div className="gender-stats">
                <span className="male-female-stat"><span className="bold italics">{adminStats.studentCount.male}</span> Males</span>
                <i className="fa-solid fa-children" style={{fontSize: '3rem', color: '#D8A7B1', transform: 'scaleX(-1)' }}></i>
                <span className="male-female-stat"><span className="bold italics">{adminStats.studentCount.female}</span> Females</span>
              </div>
            </div>
        
            {adminStats.studentsByClass.length > 0 && (
              <div className="section-grouping">
                {/* Calculate section totals */}
                {Object.entries(
                  adminStats.studentsByClass.reduce((sections, cls) => {
                    if (!sections[cls.sectionName]) {
                      sections[cls.sectionName] = {
                        total: 0,
                        male: 0,
                        female: 0,
                        classes: []
                      };
                    }
                    sections[cls.sectionName].total += cls.total;
                    sections[cls.sectionName].male += cls.male;
                    sections[cls.sectionName].female += cls.female;
                    sections[cls.sectionName].classes.push(cls);
                    return sections;
                  }, {})
                ).map(([sectionName, sectionData]) => (
                  <div key={sectionName} className="section-container">
                    <div className="stat-content-by-class">
                      <h3 className="stat-heading-d">{sectionName} Section</h3>
                      <p><span className="bold italics male-female-stat">{sectionData.total}</span> &nbsp;Students</p>
                      <div className="gender-stats">
                        <span className="male-female-stat">{sectionData.male}<span className="size6"> Males</span></span>
                        <i className="fa-solid fa-children" style={{fontSize: '2rem', color: '#E8A87C', transform: 'scaleX(-1)' }}></i>
                        <span className="male-female-stat">{sectionData.female}<span className="size6"> Females</span></span>
                      </div>
                    </div>
                    
                    <div className="stats-grid">
                      {sectionData.classes.map((cls, index) => (
                        <div key={index} className="stats-grid-by-class">
                          <div className="stat-content-by-class stat-content-by-class1">
                            <p className="bold stat-heading-d">{cls.className}</p>
                            <p><span className="bold italics male-female-stat">{cls.total}</span> &nbsp;Students</p>
                            <div className="gender-stats1">
                              <span className="male-female-stat">{cls.male}<span className="size6"> Males</span></span>
                              <i className="fa-solid fa-children" style={{fontSize: '1.5rem', color: '#E8A87C', transform: 'scaleX(-1)' }}></i>
                              <span className="male-female-stat">{cls.female}<span className="size6"> Females</span></span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
        
            {(userData.status === "Cashier" || userData.level === "Supreme") && (
              <>
                <p className="caps login-heading page-heading page-heading-d">FINANCIAL OVERVIEW</p>

                <div className="stat-content-by-class">
                  <p className="bold stat-heading-d">All Classes</p>
                  <i className="fa-solid fa-money-bill-wave" style={{fontSize: '2rem', color: '#2ECC71'}}></i>
                  <p className="male-female-stat">Expected: <span className="bold">&nbsp;₦ {adminStats.financialStats.expected.toLocaleString()}</span></p>
                  <p className="male-female-stat">Actual: <span className="bold">&nbsp;₦ {adminStats.financialStats.actual.toLocaleString()}</span></p>
                  <p className="male-female-stat">Balance: <span className="bold">&nbsp;₦ {adminStats.financialStats.balance.toLocaleString()}</span></p>
                  <p className="male-female-stat">
                    Paid: <span className="bold">&nbsp;
                    {adminStats.financialStats.expected > 0 
                      ? `${Math.round((adminStats.financialStats.actual / adminStats.financialStats.expected) * 100)}%`
                      : '0%'}
                    </span>
                  </p>
                </div>

                {adminStats.financialStatsByClass.length > 0 && (
                  <div className="section-grouping">
                    {Object.entries(
                      adminStats.financialStatsByClass.reduce((sections, cls) => {
                        if (!sections[cls.sectionName]) {
                          sections[cls.sectionName] = {
                            expected: 0,
                            actual: 0,
                            balance: 0,
                            classes: []
                          };
                        }
                        sections[cls.sectionName].expected += cls.expected;
                        sections[cls.sectionName].actual += cls.actual;
                        sections[cls.sectionName].balance += cls.balance;
                        sections[cls.sectionName].classes.push(cls);
                        return sections;
                      }, {})
                    ).map(([sectionName, sectionData]) => (
                      <div key={sectionName} className="section-container">
                        <div className="stat-content-by-class">
                          <h3 className="page-heading page-heading-d">{sectionName} Section</h3>
                          <i className="fa-solid fa-money-bill-wave" style={{fontSize: '1.5rem', color: '#2ECC71'}}></i>
                          <p className="male-female-stat">Expected: <span className="bold">&nbsp;₦ {sectionData.expected.toLocaleString()}</span></p>
                          <p className="male-female-stat">Actual: <span className="bold">&nbsp;₦ {sectionData.actual.toLocaleString()}</span></p>
                          <p className="male-female-stat">Balance: <span className="bold">&nbsp;₦ {sectionData.balance.toLocaleString()}</span></p>
                          <p className="male-female-stat">
                            Paid: <span className="bold">&nbsp;
                            {sectionData.expected > 0 
                              ? `${Math.round((sectionData.actual / sectionData.expected) * 100)}%`
                              : '0%'}
                            </span>
                          </p>
                        </div>
                        
                        <div className="stats-grid">
                          {sectionData.classes.map((cls, index) => (
                            <div key={index} className="stats-grid-by-class">
                              <div className="stat-content-by-class stat-content-by-class1">
                                <p className="bold stat-heading-d">{cls.className}</p>
                                <i className="fa-solid fa-coins" style={{fontSize: '1.5rem', color: '#F1C40F'}}></i>
                                <p className="size5 male-female-stat">Expected: <span className="bold">&nbsp;₦ {cls.expected.toLocaleString()}</span></p>
                                <p className="size5 male-female-stat">Actual: <span className="bold">&nbsp;₦ {cls.actual.toLocaleString()}</span></p>
                                <p className="size5 male-female-stat">Balance: <span className="bold">&nbsp;₦ {cls.balance.toLocaleString()}</span></p>
                                <p className="size5 male-female-stat">
                                  Paid: <span className="bold">&nbsp;
                                  {cls.expected > 0 
                                    ? `${Math.round((cls.actual / cls.expected) * 100)}%`
                                    : '0%'}
                                  </span>
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </>      
        ) : (
            <p className="no-subjects">You do not have the permission to view this page</p>
        )}
      {/* Daily Report Modal */}
      <Modal
        isOpen={showDailyReportModal}
        onRequestClose={() => {
          setShowDailyReportModal(false);
          setSelectedDate('');
        }}
        contentLabel="Daily Report"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps">Generate Daily Payment Report</p>
          <div className="modal-form">
            <label className='text-left'>Select Date</label>
            <input
              type="date"
              className="input-field"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => {
                  setShowDailyReportModal(false);
                  setSelectedDate('');
                }}
              >
                Cancel
              </button>
              <button 
                className="login-button"
                disabled={!selectedDate || generatingReport}
                onClick={generateDailyReportPDF}
              >
                {generatingReport ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Monthly Report Modal */}
      <Modal
        isOpen={showMonthlyReportModal}
        onRequestClose={() => {
          setShowMonthlyReportModal(false);
          setSelectedMonth('');
        }}
        contentLabel="Monthly Report"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps">Generate Monthly Payment Report</p>
          <div className="modal-form">
            <label className='text-left'>Select Month</label>
            <select
              className="input-field"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">Select a month</option>
              {availableMonths.map(monthKey => {
                const [year, month] = monthKey.split('-');
                return (
                  <option key={monthKey} value={monthKey}>
                    {monthNames[parseInt(month) - 1]} {year}
                  </option>
                );
              })}
            </select>
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => {
                  setShowMonthlyReportModal(false);
                  setSelectedMonth('');
                }}
              >
                Cancel
              </button>
              <button 
                className="login-button"
                disabled={!selectedMonth || generatingReport}
                onClick={generateMonthlyReportPDF}
              >
                {generatingReport ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>
        </div>
      </Modal>
      
      {showModal && 
        <SuccessModal
          icon="xmark"
          iconColor="red"
          message={error}
          onClose={() => {
            setError("");
            setShowModal(false);
          }}
        />
      }
    </div>
  );
};

export default Dashboard;
